import type { QueryClient } from '@tanstack/react-query';
import { scopeParams, type StoreScopeParams } from './endpoints';
import type { ProductAnalyticsParams } from './types';

/**
 * Central query-key vocabulary. Money-affecting mutations call
 * {@link invalidateMoneyData} — every balance on screen is recomputed by the
 * backend, so after any operation write we simply refetch everything that
 * shows money (SPEC §5 / STEP-06).
 */
export const queryKeys = {
  stores: (includeArchived = false) => ['stores', { includeArchived }] as const,
  sklads: (includeArchived = false) => ['sklads', { includeArchived }] as const,
  categories: (includeArchived = false) => ['categories', { includeArchived }] as const,
  dashboard: (scope: StoreScopeParams) => ['dashboard', scopeParams(scope)] as const,
  operations: (params: Record<string, unknown>) => ['operations', params] as const,
  monthlyReport: (month: string, scope: StoreScopeParams) =>
    ['reports', 'monthly', month, scopeParams(scope)] as const,
  skladsSummary: ['reports', 'sklads'] as const,
  ledger: (kind: 'store' | 'sklad', id: string, month: string) =>
    ['ledger', kind, id, month] as const,
  dayGrid: (kind: 'store' | 'sklad', id: string, month: string) =>
    ['reports', 'day-grid', kind, id, month] as const,
  summaryDayGrid: (month: string) => ['reports', 'summary-day-grid', month] as const,
  productAnalytics: (params: ProductAnalyticsParams, scope: StoreScopeParams) =>
    ['reports', 'products', params, scopeParams(scope)] as const,
  productShipments: (key: string, params: ProductAnalyticsParams, scope: StoreScopeParams) =>
    ['reports', 'products', 'shipments', key, params, scopeParams(scope)] as const,
  dayDetail: (kind: 'store' | 'sklad', id: string, date: string) =>
    ['reports', 'day-detail', kind, id, date] as const,
  shipments: (params: Record<string, unknown>) => ['shipments', params] as const,
  shipment: (id: string) => ['shipments', 'detail', id] as const,
  shipmentsPendingCount: (scope: StoreScopeParams) =>
    ['shipments', 'pending-count', scopeParams(scope)] as const,
  shipmentProductNames: ['shipments', 'product-names'] as const,
  shipmentLastPrices: (storeId: string) => ['shipments', 'last-prices', storeId] as const,
  movements: (params: Record<string, unknown>) => ['movements', params] as const,
  movementsDayGrid: (month: string, kind: string, scope: StoreScopeParams) =>
    ['movements', 'day-grid', month, kind, scopeParams(scope)] as const,
  moyskladStatus: ['moysklad', 'settings'] as const,
  moyskladCounterparties: ['moysklad', 'counterparties'] as const,
  moyskladWebhooks: ['moysklad', 'webhooks'] as const,
  users: ['users'] as const,
  businesses: ['businesses'] as const,
  audit: (params: Record<string, unknown>) => ['audit', params] as const,
  operationRequests: (params: Record<string, unknown>) => ['operationRequests', params] as const,
  pendingApprovals: (scope: StoreScopeParams) =>
    ['operationRequests', 'pending', scopeParams(scope)] as const,
  pendingApprovalCounts: ['operationRequests', 'pending-counts'] as const,
};

const MONEY_ROOTS = [
  'stores',
  'sklads',
  'dashboard',
  'operations',
  'reports',
  'ledger',
  'operationRequests',
];

export function invalidateMoneyData(queryClient: QueryClient): void {
  for (const root of MONEY_ROOTS) {
    void queryClient.invalidateQueries({ queryKey: [root] });
  }
}
